import { MAX_NAME_LENGTH, slugifyTrackId } from './track-id';
import type { CheckFilter } from '@internal/backstage-plugin-pulse-common';

// Track ids are kebab-case, matching the config-authored tracks.
const KEBAB_CASE_PATTERN = /^[a-z](?:[a-z0-9-]*[a-z0-9])?$/;

const isSupportedUrl = (value: string): boolean => {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

export type TrackFieldValues = {
  submitted: boolean;
  id: string;
  name: string;
  ownerEntityRef: string;
  documentationUrl: string;
  /** Selected mandatory check ids; empty strings represent unfilled rows. */
  mandatoryCheckIds: string[];
  /** Selected recommended check ids; empty strings represent unfilled rows. */
  recommendedCheckIds: string[];
  includeConditions: CheckFilter[];
  excludeConditions: CheckFilter[];
};

/**
 * Derives per-field validation errors (only surfaced once `submitted`) plus an
 * overall `isValid` flag, so the form can highlight exactly which inputs still
 * need attention instead of showing a single generic message.
 */
export const computeTrackFieldErrors = (values: TrackFieldValues) => {
  const trimmedId = values.id.trim();
  const trimmedName = values.name.trim();
  const trimmedUrl = values.documentationUrl.trim();

  const hasValidId = Boolean(trimmedId) && KEBAB_CASE_PATTERN.test(trimmedId);
  const isNameTooLong = trimmedName.length > MAX_NAME_LENGTH;
  const hasDerivableId = Boolean(slugifyTrackId(trimmedName));
  const hasValidUrl = !trimmedUrl || isSupportedUrl(trimmedUrl);
  const idFormatError =
    trimmedId.length > 0 && !KEBAB_CASE_PATTERN.test(trimmedId)
      ? 'Use kebab-case: lowercase letters, numbers and hyphens, starting with a letter and ending with a letter or number.'
      : undefined;

  const selectedMandatory = values.mandatoryCheckIds.filter(Boolean);
  const selectedRecommended = values.recommendedCheckIds.filter(Boolean);
  const hasEmptyMandatoryRow = values.mandatoryCheckIds.some(
    checkId => !checkId,
  );
  const hasEmptyRecommendedRow = values.recommendedCheckIds.some(
    checkId => !checkId,
  );
  // The pickers already prevent this; checked again so a bad payload cannot save.
  const recommendedIds = new Set(selectedRecommended);
  const hasDuplicateCheck = selectedMandatory.some(checkId =>
    recommendedIds.has(checkId),
  );

  const hasInclude = values.includeConditions.length > 0;
  const hasExclude = values.excludeConditions.length > 0;
  const excludesWithoutInclude = hasExclude && !hasInclude;

  const flag = (invalid: boolean, message: string) =>
    values.submitted && invalid ? message : undefined;

  let nameError: string | undefined;
  if (!trimmedName) {
    nameError = flag(true, 'Name is required.');
  } else if (isNameTooLong) {
    nameError = flag(
      true,
      `Name must be ${MAX_NAME_LENGTH} characters or fewer.`,
    );
  } else {
    nameError = flag(
      !hasDerivableId,
      'Name must contain letters or numbers so an id can be derived from it.',
    );
  }

  return {
    idFormatError,
    idFieldError: idFormatError ?? flag(!trimmedId, 'ID is required.'),
    nameError,
    ownerError: flag(!values.ownerEntityRef.trim(), 'Owner is required.'),
    documentationUrlError: flag(
      !hasValidUrl,
      'Enter a valid http(s) URL, or leave this blank.',
    ),
    mandatoryChecksError: flag(
      selectedMandatory.length === 0,
      'Select at least one mandatory check.',
    ),
    // Row-level messages cover these, so they only drive the invalid state.
    hasInvalidMandatoryRows: values.submitted && hasEmptyMandatoryRow,
    hasInvalidRecommendedRows: values.submitted && hasEmptyRecommendedRow,
    duplicateChecksError: flag(
      hasDuplicateCheck,
      'A check cannot be both mandatory and recommended.',
    ),
    excludeWithoutIncludeError: flag(
      excludesWithoutInclude,
      'Add at least one include filter before excluding entities.',
    ),
    isValid:
      Boolean(trimmedName) &&
      !isNameTooLong &&
      hasValidId &&
      Boolean(values.ownerEntityRef.trim()) &&
      hasValidUrl &&
      selectedMandatory.length > 0 &&
      !hasEmptyMandatoryRow &&
      !hasEmptyRecommendedRow &&
      !hasDuplicateCheck &&
      !excludesWithoutInclude,
  };
};
